<template>
    <VJoyProductTour
        ref="productTourRef"
        data-testid="profile-product-tour"
        disable-overlay
        :position="currentTour.position"
        :spotlight-selector="currentTour.selector"
        :step="currentTour.step"
        :steps="productTours.length"
        :max-width="353"
        open
        @product-tour:hide="dismiss"
    >
        <template #product-tour-preheader>
            <VJoyTag :label="t('profile.product.tour.preheader.label')" variant="special" />
        </template>
        <template #product-tour-header>
            {{ currentTour.title }}
        </template>
        <template #product-tour-content>
            {{ currentTour.content }}
        </template>
        <template #product-tour-next>
            <VJoyButton :label="currentTour.ctaLabel" size="medium" variant="main" @click="nextOrDismiss" />
        </template>
    </VJoyProductTour>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, computed, nextTick, onMounted} from 'vue';
    import {useScrollToElement, useTranslation} from '#imports';
    import {VJoyButton, VJoyProductTour, VJoyTag, type TJoyProductTourPositions} from '@maltjoy/core-vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {useWebPreferencesStore} from '~/store/webPreferences.store';

    const {scrollToAnchor} = useScrollToElement();
    const {t} = useTranslation();

    const webPreferencesStore = useWebPreferencesStore();

    onMounted(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    });

    export interface ProductTour {
        position: TJoyProductTourPositions;
        step: number;
        title: string;
        content: string;
        ctaLabel: string;
        selector: string;
    }

    const productTours: ProductTour[] = [
        {
            position: 'bottom',
            step: 1,
            title: t('profile.product.tour.step1.title'),
            content: t('profile.product.tour.step1.content'),
            ctaLabel: t('profile.product.tour.step1.cta'),
            selector: `#${anchorsPageProfile.topSkillsSetSection}`,
        },
        {
            position: 'top',
            step: 2,
            title: t('profile.product.tour.step2.title'),
            content: t('profile.product.tour.step2.content'),
            ctaLabel: t('profile.product.tour.step2.cta'),
            selector: `#${anchorsPageProfile.navigationSection}`,
        },
        {
            position: 'top',
            step: 3,
            title: t('profile.product.tour.step3.title'),
            content: t('profile.product.tour.step3.content'),
            ctaLabel: t('profile.product.tour.step3.cta'),
            selector: `#${anchorsPageProfile.aboutSection}`,
        },
        {
            position: 'top',
            step: 4,
            title: t('profile.product.tour.step4.title'),
            content: t('profile.product.tour.step4.content'),
            ctaLabel: t('profile.product.tour.step4.cta'),
            selector: `#${anchorsPageProfile.shareProfileButton}`,
        },
    ];

    const productTourRef = useTemplateRef('productTourRef');
    const currentStepIndex = ref(0);
    const currentTour = computed<ProductTour>(() => productTours[currentStepIndex.value]);

    async function dismiss() {
        await webPreferencesStore.acknowledgeProfileProductTour();
    }

    async function nextOrDismiss() {
        productTourRef.value?.hide();
        const isLastStep = currentStepIndex.value >= productTours.length - 1;
        if (isLastStep) {
            await dismiss();
            return;
        }
        currentStepIndex.value++;
        await nextTick();
        productTourRef.value?.show();
        scrollToSpecificStep();
    }

    function scrollToSpecificStep() {
        const ABOUT_SECTION_STEP_INDEX = 2;
        const OFFSET = 300;

        // specific scroll for about section
        if (currentStepIndex.value === ABOUT_SECTION_STEP_INDEX) {
            scrollToAnchor(currentTour.value.selector, OFFSET);
        } else {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    }
</script>

<style lang="scss">
    .joy-product-tour__footer {
        display: flex !important;
        justify-content: flex-end !important;
        gap: 30px !important;
    }
</style>
